import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowForward } from '@mui/icons-material';
import { Box, Card, CardContent, IconButton, Tooltip } from '@mui/material';
import { common } from '@mui/material/colors';
import { observer } from 'mobx-react-lite';
import { FuseInput, FuseLogo } from '/src/components';
import { useUserStore } from '/src/context';
import { DesktopLayout } from '/src/layouts';
import { FormErrors } from '/src/lib/types';

/**
 * Sign In Page
 */
export const SigninPage: React.FC = observer(() => {
  /** Context **/
  const userStore = useUserStore();
  const navigate = useNavigate();

  /** State **/
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const [, setSigninError] = useState('');

  /** Methods **/

  /**
   * Validate credentials and log the given User in if valid.
   *
   * @remarks
   * Redirects to the Dashboard after login.
   */
  const signin = async () => {
    const errors: FormErrors = {};
    if (!username) errors.username = 'Username is required.';
    if (!password) errors.password = 'Password is required.';
    if (Object.keys(errors).length) {
      setFormErrors(errors);
      return;
    }

    try {
      await userStore.signin(username, password);

      navigate('/');
    } catch (err) {
      setSigninError((err as Error).message);
    }
  };

  /** Effects **/
  useEffect(() => {
    if (userStore.user) navigate('/');
  }, []);

  /** Render **/
  return (
    <DesktopLayout>
      <section>
        <form noValidate name="userForm" id="userForm">
          <Box className="detail-page-content margin-top">
            <Box display="flex">
              <Box flex="35" />

              <Box flex="30">
                <Card component="md-card">
                  <Box sx={{ background: common.white }}>
                    <CardContent style={{ padding: '16px' }}>
                      <FuseLogo style={{ maxWidth: '100%' }} />
                    </CardContent>
                    <CardContent>
                      <Box display="flex" justifyContent="center" alignItems="center">
                        <FuseInput
                          id="username"
                          label="Username"
                          value={username}
                          updateValue={(value) => setUsername(value)}
                          error={formErrors.username}
                          onPressEnter={() => signin()}
                        />
                      </Box>
                      <Box display="flex" justifyContent="center" alignItems="center">
                        <FuseInput
                          id="password"
                          label="Password"
                          value={password}
                          updateValue={(value) => setPassword(value)}
                          type="password"
                          error={formErrors.password}
                          onPressEnter={() => signin()}
                        />
                      </Box>

                      <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Link to="/password/forgot" className="md-caption">
                          Forgot your password?
                        </Link>

                        <IconButton className="md-fab md-mini" aria-label="Sign In" onClick={() => signin()}>
                          <Tooltip title="Sign In">
                            <ArrowForward className="material-icons" />
                          </Tooltip>
                        </IconButton>
                      </Box>
                    </CardContent>
                  </Box>
                </Card>
              </Box>

              <Box flex="35" />
            </Box>
          </Box>
        </form>
      </section>
    </DesktopLayout>
  );
});
